/* @jsx h */
import { defineComponent, h, Fragment, ref } from "vue";

import { useAuth } from "@app/vue/providers/Auth";
import {
  NavLink,
  navItemClass,
  navItemContentClass,
  navItemIconClass,
  navItemTextClass,
} from "./NavLink";
import { NavGroup } from "./NavGroup";
import { PrinterSetupWizard } from "@app/vue/pages/Devices";
import { useLocalization, SwissMade } from "@lemmonhq/uikit";

export const MobileNav = defineComponent({
  name: "MobileNav",
  props: {
    posMode: { type: Boolean, default: false },
    onLinkClick: { type: Function, required: true },
  },
  setup(props) {
    const auth = useAuth();
    const { t } = useLocalization();
    const showPrinterSetupWizard = ref(false);

    const renderPrinterSetupTrigger = () => (
      <button
        onClick={() => {
          showPrinterSetupWizard.value = true;
        }}
        class={`${navItemClass(false)} text-left`}
      >
        <span class={navItemContentClass}>
          <span class={navItemIconClass}>print_add</span>
          <span class={navItemTextClass}>Printer setup</span>
        </span>
      </button>
    );

    const renderReservationsNav = () => (
      <NavGroup
        title={t("nav.labels.reservations")}
        icon="calendar_month"
        parentPath="/app/manager/reservations"
      >
        <NavLink
          to="/app/manager/reservations"
          icon="table_restaurant"
          title="reservations.nav.requests"
          parents={[true]}
          onClick={props.onLinkClick}
        />
        <NavLink
          to="/app/manager/reservations/customers"
          icon="group"
          title="reservations.customers.title"
          parents={[true]}
          onClick={props.onLinkClick}
        />
        <NavLink
          to="/app/manager/reservations/history"
          icon="history"
          title="reservations.nav.history"
          parents={[true]}
          onClick={props.onLinkClick}
        />
      </NavGroup>
    );

    const renderReportsNav = () => {
      const isManager = auth.user.value?.role === "manager";
      const isStaging = import.meta.env.VITE_ENV === "staging";

      return (
        <NavGroup
          title={t("nav.labels.reports")}
          icon="devices"
          parentPath="/app/reports"
        >
          <NavLink
            to="/app/reports/live"
            icon="bolt"
            title="reports.nav.live"
            parents={[true]}
            onClick={props.onLinkClick}
          />
          <NavLink
            to="/app/reports"
            icon="show_chart"
            title="reports.nav.sales_dashboard"
            parents={[true]}
            onClick={props.onLinkClick}
          />
          <NavLink
            to="/app/reports/transactions?responsive=true"
            icon="history"
            title="reports.nav.payment_history"
            parents={[true]}
            onClick={props.onLinkClick}
          />
          <NavLink
            to="/app/reports/tips"
            icon="savings"
            title="reports.nav.tips"
            parents={[true]}
            onClick={props.onLinkClick}
          />
          <NavLink
            to="/app/reports/closing_reports"
            icon="sunny"
            title="reports.nav.closing_reports"
            parents={[true]}
            onClick={props.onLinkClick}
          />
          {isManager ? (
            <Fragment>
              <NavLink
                to="/app/reports/revenue"
                icon="stacked_line_chart"
                title="reports.nav.revenue"
                parents={[true]}
                onClick={props.onLinkClick}
              />
              <NavLink
                to="/app/reports/shifts"
                icon="person"
                title="reports.nav.shifts"
                parents={[true]}
                onClick={props.onLinkClick}
              />
              <NavLink
                to="/app/reports/weekly_heatmap"
                icon="date_range"
                title="reports.nav.weekly_sales"
                parents={[true]}
                onClick={props.onLinkClick}
              />
              <NavLink
                to="/app/reports/reservations"
                icon="event_available"
                title="reports.nav.reservations"
                parents={[true]}
                onClick={props.onLinkClick}
              />
              <NavLink
                to="/app/reports/dataroom"
                icon="database"
                title="reports.nav.dataroom"
                parents={[true]}
                onClick={props.onLinkClick}
              />
              {isStaging ? (
                <NavLink
                  to="/app/reports/chart_of_accounts"
                  icon="account_tree"
                  title="reports.nav.chart_of_accounts"
                  parents={[true]}
                  onClick={props.onLinkClick}
                />
              ) : null}
              <NavLink
                to="/app/reports/export"
                icon="file_export"
                title="reports.nav.export"
                parents={[true]}
                onClick={props.onLinkClick}
              />
            </Fragment>
          ) : (
            <NavLink
              to="/app/reports/shifts"
              icon="person"
              title="reports.nav.shifts"
              parents={[true]}
              onClick={props.onLinkClick}
            />
          )}
        </NavGroup>
      );
    };

    const renderSettingsNav = () => {
      const isStaging = import.meta.env.VITE_ENV === "staging";

      return (
        <NavGroup
          title={t("nav.labels.settings")}
          icon="settings"
          parentPath="/app/settings"
        >
          <NavLink
            to="/app/settings"
            icon="tune"
            title="settings.nav.title"
            parents={[true]}
            onClick={props.onLinkClick}
          />
          <NavLink
            to="/app/settings/profile"
            icon="restaurant"
            title="settings.nav.profile"
            parents={[true]}
            onClick={props.onLinkClick}
          />
          <NavLink
            to="/app/settings/reservations"
            icon="calendar_month"
            title="settings.nav.reservations"
            parents={[true]}
            onClick={props.onLinkClick}
          />
          <NavLink
            to="/app/settings/discount-types"
            icon="local_offer"
            title="settings.discount_types_page.page_title"
            parents={[true]}
            onClick={props.onLinkClick}
          />
          <NavLink
            to="/app/settings/payments"
            icon="payment"
            title="settings.nav.payments"
            parents={[true]}
            onClick={props.onLinkClick}
          />
          <NavLink
            to="/app/settings/print-templates"
            icon="receipt_long"
            title="Print templates"
            parents={[true]}
            onClick={props.onLinkClick}
          />
          <NavLink
            to="/app/settings/audit"
            icon="history"
            title="Audit"
            parents={[true]}
            onClick={props.onLinkClick}
          />
          {isStaging ? (
            <Fragment>
              <NavLink
                to="/app/settings/staff"
                icon="group"
                title="settings.nav.staff"
                parents={[true]}
                onClick={props.onLinkClick}
              />
              <NavLink
                to="/app/settings/taxes"
                icon="money"
                title="settings.nav.taxes"
                parents={[true]}
                onClick={props.onLinkClick}
              />
            </Fragment>
          ) : null}
        </NavGroup>
      );
    };

    const renderPOSNav = () => (
      <Fragment>
        <NavLink
          to="/app/pos"
          icon="computer"
          title="nav.core.pos"
          onClick={props.onLinkClick}
        />
        <NavLink
          to="/app/tables"
          icon="table_restaurant"
          title="nav.core.tables"
          onClick={props.onLinkClick}
        />

        {renderReservationsNav()}
        {renderPrinterSetupTrigger()}
        {renderReportsNav()}
      </Fragment>
    );

    const renderManagerNav = () => (
      <Fragment>
        {renderReportsNav()}
        <NavLink
          to="/app/manager/loyalty"
          icon="social_leaderboard"
          title="nav.labels.loyalty"
          onClick={props.onLinkClick}
        />
        {renderReservationsNav()}
        {renderPrinterSetupTrigger()}
        <NavLink
          to="/manager/products"
          icon="category"
          title="manager.nav.dashboard"
          external
          onClick={props.onLinkClick}
        />
        <NavLink
          to="/manager/tables"
          icon="table_restaurant"
          title="nav.core.tables"
          external
          onClick={props.onLinkClick}
        />
        {renderSettingsNav()}
      </Fragment>
    );

    return () => {
      //console.log("a", auth.user.value);
      if (!auth.user.value) return null;
      return (
        <>
          <div style={{}} class="flex-1 flex flex-col space-y-5 select-none ">
            <div class="flex flex-col flex-1 overflow-y-auto overflow-x-hidden px-4">
              {auth.user.value.role === "staff" ? renderPOSNav() : null}
              {auth.user.value.role === "manager"
                ? props.posMode
                  ? renderPOSNav()
                  : renderManagerNav()
                : null}
            </div>
            <div class=" py-4 border-t  flex">
              <div class="text-xs flex-1 flex items-end px-2">
                <p class="hidden">
                  <a
                    target="_blank"
                    href="https://www.thelemmon.ch"
                    class=" text-prominent"
                  >
                    Qari Sarl <br /> Avenue du Léman 2, 1005 Lausanne
                  </a>
                </p>
                <p class="text-accent">
                  Copyright © 2026{" "}
                  <span>
                    {" | "}
                    Made with <span class="text-[#ed2224]">♥</span>
                  </span>
                </p>
                <p class="text-prominent"></p>
              </div>
              <div class="px-4 flex items-start justify-end mb-1">
                <SwissMade width={50} />
              </div>
            </div>
          </div>

          {showPrinterSetupWizard.value && (
            <PrinterSetupWizard
              onClose={() => {
                showPrinterSetupWizard.value = false;
              }}
              onSuccess={() => {
                showPrinterSetupWizard.value = false;
              }}
            />
          )}
        </>
      );
    };
  },
});
